import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mic, Square, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { stopReading } from "@/lib/readingAudio";

const MAX_MS = 5 * 60 * 1000;
const pickType = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const W = {
  en: { go: "I read aloud", done: "I've finished", saving: "Saving…", rule: "Read the text aloud: the timer and the recording start together. Your parents can listen to you. Only readings with your voice count for badges and your reading streak.",
    noMic: "No microphone: the timer still works, but this reading will not count for badges or the streak." },
  fr: { go: "Je lis à voix haute", done: "J'ai fini", saving: "Enregistrement…", rule: "Lis le texte à voix haute : le chrono et l'enregistrement démarrent ensemble. Tes parents pourront t'écouter. Seules les lectures avec ta voix comptent pour les badges et ta série de jours.",
    noMic: "Pas de micro : le chrono marche quand même, mais cette lecture ne comptera pas pour les badges ni la série." },
};

// Library: timer + voice recording in one go, the recording proves the child really read
export default function LibraryReadAloud({ childId, t, lang, onResult, onBegin }) {
  const w = W[lang] || W.en;
  const [state, setState] = useState("idle");
  const [start, setStart] = useState(null);
  const [now, setNow] = useState(0);
  const rec = useRef(null);
  const tick = useRef(null);
  const cap = useRef(null);
  const cleanup = () => { clearInterval(tick.current); clearTimeout(cap.current); };
  useEffect(() => () => { cleanup(); if (rec.current?.state === "recording") { rec.current.onstop = null; rec.current.stop(); } }, []);

  const send = async (secs, recordingId) => {
    try {
      const { data } = await api.post(`/reading/${childId}/text/${t.id}/read`, { seconds: secs, recording_id: recordingId });
      onResult(data);
    } catch { toast.error(lang === "fr" ? "Oups, réessaie." : "Oops, try again."); }
    setState("idle");
  };
  const clock = () => { onBegin(); stopReading(); const s = Date.now(); setStart(s); setNow(s); tick.current = setInterval(() => setNow(Date.now()), 250); return s; };

  const go = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {
      toast.error(w.noMic);
      clock();
      setState("timer");
      return;
    }
    const type = pickType();
    const r = new MediaRecorder(stream, { ...(type ? { mimeType: type } : {}), audioBitsPerSecond: 32000 });
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const s = clock();
    r.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      const secs = Math.max(1, Math.round((Date.now() - s) / 1000));
      setState("saving");
      const blob = new Blob(chunks, { type: (r.mimeType || type || "audio/webm").split(";")[0] });
      const fd = new FormData();
      fd.append("story_id", t.id);
      fd.append("page", "0");
      fd.append("file", blob, `read.${blob.type.includes("mp4") ? "m4a" : blob.type.split("/")[1]}`);
      let id = null;
      try { id = (await api.post(`/reading/${childId}/recordings`, fd)).data.id; } catch { toast.error(lang === "fr" ? "L'enregistrement n'a pas été sauvegardé." : "The recording was not saved."); }
      await send(secs, id);
    };
    rec.current = r;
    r.start(1000);
    setState("recording");
    cap.current = setTimeout(() => r.state === "recording" && finish(), MAX_MS);
  };
  const finish = () => {
    cleanup();
    setStart(null);
    if (state === "timer") return send(Math.max(1, Math.round((Date.now() - start) / 1000)), null);
    if (rec.current?.state === "recording") rec.current.stop();
  };
  const secs = start ? Math.floor((now - start) / 1000) : 0;
  return (
    <div className="space-y-3" data-testid="library-read-aloud">
      <p className="text-sm font-semibold text-slate-600" data-testid="library-read-rule">{w.rule}</p>
      <div className="flex flex-wrap items-center gap-3">
        {state === "idle" && (
          <motion.button whileTap={{ scale: 0.95 }} onClick={go} data-testid="text-start"
            className="rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold bg-pink-500 text-white inline-flex items-center gap-2 shadow-[4px_4px_0_0_#0F172A]">
            <Mic size={18} /> {w.go}
          </motion.button>
        )}
        {(state === "recording" || state === "timer") && (
          <>
            {state === "recording" && <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 1.2 }} className="w-3 h-3 rounded-full bg-red-600" data-testid="library-rec-dot" />}
            <span className="text-3xl font-bold tabular-nums text-green-700" data-testid="text-timer"><Timer className="inline -mt-1" /> {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
            <motion.button whileTap={{ scale: 0.95 }} onClick={finish} data-testid="text-done"
              className="rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold bg-green-500 text-white inline-flex items-center gap-2 shadow-[4px_4px_0_0_#0F172A]">
              <Square size={16} /> {w.done}
            </motion.button>
          </>
        )}
        {state === "saving" && <span className="font-bold inline-flex items-center gap-2" data-testid="library-saving"><Loader2 className="animate-spin" /> {w.saving}</span>}
      </div>
    </div>
  );
}
